import { Moment } from "moment";
import { CalendarEntry, CalendarTimeType } from "mueller-controls";

export const CalendarBreakVars = {
  minEntryLengthHours: 3, // smallest entry a user can make on their own
  breakPaddingHours: 2, // how far away from the start or end of a shift a break has to start or end
  maxConsecutiveHours: 5, // longest a shift can get without a break (this is used in logic letting you select breaks, and used to know when an event needs to be broken up)
  maxEntryLengthHours: 10, // this cannot exceed maxConsecutiveHours * 2 + breakLengthHours, we decided to set it to 10 for now

  breakChoiceGranularityHours: .5, // let them pick breaks in 30 minute intervals
  breakLengthHours: .5, // breaks are 30 minutes long
};

export interface BreakOption {
  startTime: Moment;
  endTime: Moment;
  display: string;
  startEvent: CalendarEntry;
  endEvent: CalendarEntry;
  breakEvent: CalendarEntry;
};

export const getBreakOptions = (event: CalendarEntry) => {
  let { breakPaddingHours, maxConsecutiveHours, breakChoiceGranularityHours, breakLengthHours } = CalendarBreakVars;

  let duration = event.duration;
  if (duration >= maxConsecutiveHours) {
    // all the following logic treats the beginning of the event as 0 hours, and the end as 'duration' hours
    // dont let them pick anywhere within the minimum padding, and dont let them pick something so early that it would make the end of their shift too long
    let breakChoicesStart = Math.max(duration - maxConsecutiveHours - breakLengthHours, breakPaddingHours);
    let breakChoicesEnd = Math.min(maxConsecutiveHours, duration - breakPaddingHours);

    // this should never happen unless someone changes the settings variables to not make sense, but if it does happen, they will just get one break choice in the middle of their shift
    if (breakChoicesEnd <= breakChoicesStart) {
      breakChoicesEnd = breakChoicesStart + breakLengthHours;
    }

    let breakOptions: BreakOption[] = [];

    for (let nextBreakTime = breakChoicesStart; nextBreakTime <= breakChoicesEnd; nextBreakTime += breakChoiceGranularityHours) {
      let startTime = event.startDate.clone().add(nextBreakTime, "hours");
      let endTime = startTime.clone().add(breakLengthHours, "hours");
      const timeFormat = "h:mmA";
      let display = `${startTime.format(timeFormat)} - ${endTime.format(timeFormat)}`;
      let startEvent = event.clone();
      startEvent.endDate = startTime.clone(); // end of the event is the start of the break
      let endEvent = event.clone();
      endEvent.startDate = endTime.clone();
      let breakEvent = event.clone();
      breakEvent.startDate = startEvent.endDate;
      breakEvent.endDate = endEvent.startDate;
      breakEvent.timeType = CalendarTimeType.Break;

      breakOptions.push({
        startTime,
        endTime,
        display,
        startEvent,
        endEvent,
        breakEvent,
      });
    }

    return {
      breakRequired: true,
      breakOptions: breakOptions,
    };
  } else {
    return {
      breakRequired: false,
      breakOptions: null,
    };
  }
};

// Checks if newEntry overlaps and existingEntries, and returns the first overlapping event it finds
// 
export const getFirstEntryOverlap = (newEntry: CalendarEntry, eventPaddingHours: number, existingEntries: CalendarEntry[]) => {
  /*
  need an algorithm that figures out if events are butting up against each other (even just within the max alotted break time)
	add the 'break time' on the start and end of event comparing
	call function to break up reocurring event into its pieces?
		this function doesnt exist, but we do have: getCalEntriesForDay/event.fallsOnDay
	call that func on every other event we have downloaded
	then compare for isBefore/After - OrEqual (maybe not orequal because we added the break time padding)
  */

  let overlap: ReturnType<CalendarEntry["overlapsEvent"]>;

  let entryWithBreak = newEntry.clone();
  entryWithBreak.startDate.subtract(eventPaddingHours, "hours");
  entryWithBreak.endDate.add(eventPaddingHours, "hours");

  let overlappedEntry = existingEntries.find(e => {
    overlap = e.overlapsEvent(entryWithBreak);
    if (overlap.overlaps) return true;
    else return false;
  });

  return {
    foundOverlappingEntry: !!overlappedEntry,
    entry: overlappedEntry,
    onDate: overlap?.dateOfOverlap,
  };
};
